/**
 * ONE place that knows every React Query cache an emission-factor write can
 * invalidate.
 *
 * WHY THIS EXISTS. Emission-factor data is read by nine differently-keyed
 * queries spread across four features — the two settings grids, the table
 * detail page, the expense form's carbon picker, the account carbon settings
 * panel, and the places utilities override fields. Before this module each
 * writer hand-rolled its own invalidation list, and they had drifted: the
 * detail page invalidated seven keys, the picker's Climatiq flow seven
 * (a different seven), `useCreateEmissionFactorTable` exactly one, and
 * `useSaveEmissionFactorPick` none at all. A factor written through one
 * surface therefore went unseen by another until its staleTime expired.
 *
 * The rule now: EVERY emission-factor write calls
 * `invalidateEmissionFactorCaches(queryClient, orgId)` and nothing else. It is
 * deliberately a SUPERSET of what any single writer needs — an emission-factor
 * write is rare (settings-area administration), the caches are small, and a
 * missed refresh is a support ticket while an extra refetch is imperceptible.
 *
 * NO `captureException` here or in the mutation hooks that call it: per
 * docs/OBSERVABILITY.md a failed user-facing mutation reports through a toast,
 * not Sentry.
 */
import type { QueryClient } from '@tanstack/react-query';

/**
 * The read scope that means "the SYSTEM (global, `organization_id IS NULL`)
 * tables only" — System Settings, super admins, no organization. It takes the
 * org slot of every emission-factor key and read path, so the three index
 * hooks accept either an org id or this sentinel.
 */
export const EMISSION_FACTOR_SYSTEM_SCOPE = 'system' as const;

/** An organization id, or `EMISSION_FACTOR_SYSTEM_SCOPE`. */
export type EmissionFactorReadScope = string;

/**
 * The API base the three emission-factor reads hang off: the org-less
 * super-admin `/api/v1/emissions/system` for the system scope, else the org's
 * `/api/v1/emissions/orgs/:orgId` (same response shapes under both).
 */
export function emissionFactorReadBase(scope: EmissionFactorReadScope | undefined): string {
  return scope === EMISSION_FACTOR_SYSTEM_SCOPE
    ? '/api/v1/emissions/system'
    : `/api/v1/emissions/orgs/${scope}`;
}

/**
 * The org-scoped prefixes. Each is the FIRST TWO slots of a live query key, so
 * `invalidateQueries` matches every sub-key beneath it:
 *
 * - `tables(orgId)`   → `['emission-factor-tables', orgId, 'index', {}]`
 *                       (`useEmissionFactorTableIndex`, and the legacy
 *                       `'list'` sub-key until it is deleted)
 * - `factors(orgId)`  → `['emission-factors', orgId, 'index', { tableId }]`
 *                       (`useEmissionFactorIndex`, EVERY table at once)
 * - `columns(orgId)`  → `['emission-factor-columns', orgId, tableId]`
 *                       (`useEmissionFactorTableColumns`)
 *
 * The TABLES prefix matters after a FACTOR write too: the tables index carries
 * per-table rollups (`factor_count`, `active_factor_count`,
 * `factors_missing_input_unit`, `latest_factor_created_at`), so adding or
 * soft-deleting a factor changes a row of the LIST as well as the detail grid.
 */
export const emissionFactorKeys = {
  tables: (orgId: string | undefined) => ['emission-factor-tables', orgId] as const,
  factors: (orgId: string | undefined) => ['emission-factors', orgId] as const,
  columns: (orgId: string | undefined) => ['emission-factor-columns', orgId] as const,
};

/**
 * The org-AGNOSTIC picker caches. These predate the index hooks and are keyed
 * by `tableId` (or by a dimension key) with NO org slot, so they can only be
 * invalidated by their bare prefix — which is correct, since a user only ever
 * has one org's data in the cache per table id.
 *
 * All seven are read by `useCarbonConfig`: the two column reads the picker
 * still makes directly, the input-unit lookup, the cascade's dimension rows,
 * and the two resolver queries.
 */
const PICKER_CACHE_PREFIXES = [
  ['carbon-table-columns'],
  ['carbon-column-values'],
  ['factor-table-input-unit'],
  ['factor-dimension-rows'],
  ['dimension-resolved-factor-unit'],
  ['resolved-factor'],
] as const;

/**
 * Invalidate every cache that could hold stale emission-factor data.
 *
 * Call from the `onSuccess` of ANY emission-factor write — table create /
 * soft-delete / restore, factor create / update / delete, column and
 * column-value writes, and the Climatiq snapshot pick.
 *
 * RETIRED KEYS ARE DELIBERATELY ABSENT. Nothing here invalidates
 * `['emission-factors', tableId]`, `['emission-factor-table', tableId]`,
 * `['table-columns', tableId]`, `['column-values', …]`,
 * `['emission-factor-tables', orgId, 'list' | 'picker']` or
 * `['emission-factor-tables-for-accounts']`: those keys have no reader left.
 * Note `['emission-factors', orgId]` is NOT the retired
 * `['emission-factors', tableId]` — same first slot, different second, and the
 * old key can never match the new one because slot [1] is now an ORG id.
 *
 * SYSTEM SCOPE → BARE PREFIXES. A write to a system (global) table changes
 * rows every org's index also shows, so `EMISSION_FACTOR_SYSTEM_SCOPE`
 * invalidates `['emission-factor-tables']`, `['emission-factors']` and
 * `['emission-factor-columns']` — every scope at once, the system one included.
 *
 * Fire-and-forget: `invalidateQueries` marks matches synchronously and the
 * refetch is what the returned promise awaits, so callers do not need it.
 */
export function invalidateEmissionFactorCaches(
  queryClient: QueryClient,
  scope: EmissionFactorReadScope | undefined,
): void {
  const scoped = <K extends readonly [string, string | undefined]>(key: K) =>
    scope === EMISSION_FACTOR_SYSTEM_SCOPE ? [key[0]] : key;
  void queryClient.invalidateQueries({ queryKey: scoped(emissionFactorKeys.tables(scope)) });
  void queryClient.invalidateQueries({ queryKey: scoped(emissionFactorKeys.factors(scope)) });
  void queryClient.invalidateQueries({ queryKey: scoped(emissionFactorKeys.columns(scope)) });
  for (const prefix of PICKER_CACHE_PREFIXES) {
    void queryClient.invalidateQueries({ queryKey: prefix });
  }
}
